import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WhatsAppIdentitySlot, WhatsAppSessionHealth } from '@optiflow/types';
import { api } from '@/services/apiClient';

/** One identity as the settings screen needs it. Nothing secret is ever in this shape. */
export interface WhatsAppIdentityView {
  identitySlot: WhatsAppIdentitySlot;
  phoneNumber: string | null;
  phoneNormalized: string | null;
  configured: boolean;
  status: WhatsAppSessionHealth;
  requiresReauth: boolean;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;
  lastSafeErrorCode: string | null;
  usable: boolean;
}

export interface WhatsAppAlertView {
  kind: 'PRIMARY_UNAVAILABLE' | 'NONE';
  messageKey: string | null;
  actions: readonly string[];
  canSwitchToAlternative: boolean;
}

export interface WhatsAppSessionView {
  branchId: string;
  activeIdentity: WhatsAppIdentitySlot;
  automationEnabled: boolean;
  manualFallbackEnabled: boolean;
  primary: WhatsAppIdentityView;
  alternative: WhatsAppIdentityView;
  alert: WhatsAppAlertView;
}

export interface BranchWhatsAppConfigView {
  id: string;
  branchId: string;
  enabled: boolean;
  primaryPhoneNumber: string;
  primaryPhoneNormalized: string;
  alternativePhoneNumber: string | null;
  alternativePhoneNormalized: string | null;
  activeIdentity: WhatsAppIdentitySlot;
  automationEnabled: boolean;
  manualFallbackEnabled: boolean;
  sessionHealth: WhatsAppSessionHealth;
  automationEligible: boolean;
  version: number;
}

const sessionKey = (branchId: string) => ['whatsapp', 'session', branchId] as const;
const configKey = (branchId: string) => ['whatsapp', 'config', branchId] as const;

/**
 * The branch's two identities and whatever alert they warrant.
 *
 * Polled while a session is mid-flight, because connecting is a conversation with WhatsApp that
 * finishes on its own schedule and nothing pushes the result back to this tab.
 */
export function useWhatsAppSessionQuery(branchId: string | null, poll: boolean) {
  return useQuery({
    queryKey: sessionKey(branchId ?? ''),
    queryFn: () => api.get<WhatsAppSessionView>(`/crm/whatsapp/branches/${branchId}/session`),
    enabled: Boolean(branchId),
    refetchInterval: poll ? 3_000 : false,
  });
}

export function useBranchWhatsAppConfigQuery(branchId: string | null) {
  return useQuery({
    queryKey: configKey(branchId ?? ''),
    queryFn: () => api.get<BranchWhatsAppConfigView>(`/crm/branches/${branchId}/whatsapp`),
    enabled: Boolean(branchId),
    // A branch with nothing set up yet answers 404, which is an answer rather than a fault.
    retry: false,
  });
}

/**
 * The live pairing code.
 *
 * Deliberately never cached and never persisted: it is short-lived linking material, and the only
 * copy that should exist outside the server is the one currently on screen.
 */
export function usePairingQuery(
  branchId: string | null,
  slot: WhatsAppIdentitySlot,
  active: boolean,
) {
  return useQuery({
    queryKey: ['whatsapp', 'pairing', branchId ?? '', slot],
    queryFn: () =>
      api.get<{ qr: string | null; status: WhatsAppSessionHealth }>(
        `/crm/whatsapp/branches/${branchId}/session/pairing?identitySlot=${slot}`,
      ),
    enabled: Boolean(branchId) && active,
    refetchInterval: active ? 2_000 : false,
    gcTime: 0,
    staleTime: 0,
  });
}

type SessionAction = 'connect' | 'disconnect' | 'logout' | 'reconnect';

export function useSessionActionMutation(branchId: string | null) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ action, slot }: { action: SessionAction; slot: WhatsAppIdentitySlot }) =>
      api.post<WhatsAppSessionView>(`/crm/whatsapp/branches/${branchId}/session/${action}`, {
        identitySlot: slot,
      }),
    onSuccess: (view) => {
      client.setQueryData(sessionKey(branchId ?? ''), view);
      void client.invalidateQueries({ queryKey: configKey(branchId ?? '') });
    },
  });
}

/** Moving the branch to its other number. Explicit, reasoned, and refused unless that one is live. */
export function useSwitchIdentityMutation(branchId: string | null) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { activeIdentity: WhatsAppIdentitySlot; reason: string; version: number }) =>
      api.post<BranchWhatsAppConfigView>(`/crm/branches/${branchId}/whatsapp/identity`, input),
    onSuccess: (config) => {
      client.setQueryData(configKey(branchId ?? ''), config);
      void client.invalidateQueries({ queryKey: sessionKey(branchId ?? '') });
    },
  });
}

/** Whether a status is one the screen should keep polling through. */
export function isSettling(status: WhatsAppSessionHealth): boolean {
  return status === 'CONNECTING' || status === 'QR_REQUIRED';
}
